import { updateDoc, getFirestore, setDoc, addDoc, deleteDoc, doc, collection as firestoreCollection, Timestamp } from "firebase/firestore";
import { DataCreator } from "@utils/dataCreator";
import { z, ZodObject } from "zod";
import { LocalData } from "../../to-be-removed/LocalData/General/LocalData";
import { removeAllUndefinedFields } from "@src/pages/FilesPage/FilesPageList/ExpandedFileElement/ExpandedFileElement.utilities";

/**
 * @deprecated make using userId from getCurrentUser()
 */
export const makeCreatedBy = () => ({
  userID: LocalData.currentUser.id,
  date: new Date(),
});

/**
 * @deprecated use `updateDoc` from `firebase/firestore` directly
 */
export const update = <Schema extends z.ZodSchema, TData = z.infer<Schema>>(
  collection: string,
  validationSchema: Schema,
  documentId: string,
  data: Partial<TData>,
  forceSavedFields?: (keyof TData)[]
) => {
  const validation = (validationSchema as unknown as ZodObject<any>).partial().safeParse(data);

  if (validation.success) {
    const dataToSave: { [key: string]: any } = removeAllUndefinedFields(validation.data);
    if (forceSavedFields) {
      for (const field of forceSavedFields) {
        if (data[field] !== undefined) {
          dataToSave[field as string] = data[field];
        }
      }
    }

    return updateDoc(doc(getFirestore(), collection, documentId), {
      ...validation.data,
      updatedBy: {
        userID: LocalData.currentUser.id,
        date: Timestamp.fromDate(new Date()),
      },
    }).then(() => void 0);
  } else {
    throw validation.error;
  }
};

/**
 * @deprecated use `setDoc` from `firebase/firestore` directly
 */
export const set = <Schema extends z.ZodSchema, TData = z.infer<Schema>>(
  collection: string,
  validationSchema: Schema,
  documentId: string,
  data: Partial<TData>,
  merge: boolean
) => {
  const validation = (validationSchema as unknown as ZodObject<any>).partial().safeParse(data);

  if (validation.success) {
    return setDoc(
      doc(getFirestore(), collection, documentId),
      {
        ...validation.data,
        updatedBy: {
          userID: LocalData.currentUser.id,
          date: Timestamp.fromDate(new Date()),
        },
      },
      { merge }
    ).then(() => void 0);
  } else {
    throw validation.error;
  }
};

/**
 * @deprecated use `addDoc` from `firebase/firestore` directly
 */
export const create = <Schema extends z.ZodSchema, TData = z.infer<Schema>>(
  collection: string,
  validationSchema: Schema,
  data: Partial<TData>,
  customDocumentId?: string
) => {
  const validation = validationSchema.safeParse(data);

  const onlyMissingDocumentId =
    !validation.success && validation.error && validation.error.errors.length === 1 && validation.error.errors[0].path[0] === "id";

  if (validation.success || onlyMissingDocumentId) {
    if (customDocumentId) {
      return setDoc(doc(getFirestore(), collection, customDocumentId), {
        ...data,
        createdBy: DataCreator.getCreatedBy(),
      }).then(() => undefined);
    } else {
      return addDoc(firestoreCollection(getFirestore(), collection), {
        ...data,
        createdBy: DataCreator.getCreatedBy(),
      }).then((data) => data.id);
    }
  } else {
    throw validation.error;
  }
};

/**
 * @deprecated use `deleteDoc` from `firebase/firestore` directly
 */
export const del = (collection: string, documentId: string) => {
  return deleteDoc(doc(getFirestore(), collection, documentId));
};
